/**
 * Responsibility Specs
 *
 * The declarative layer of the reaction engine: a defender's job described as DATA — what
 * latches he tracks, what makes him change his mind, and how he moves in each state — executed
 * by one shared interpreter.
 *
 * Read policies were previously eight hand-rolled state machines. Each repeated the same
 * skeleton (loop the feed, classify events, switch state, pick a target, integrate movement),
 * which meant adding a coverage meant writing that skeleton again and wiring a new branch into
 * `resolvePositionMovement`. A spec replaces the skeleton with a description:
 *
 *     latches      what he remembers  ("he broke off short")
 *     transitions  what changes his mind  (first match wins, evaluated in order)
 *     behavior     how he moves in each state  (target + speed + technique)
 *
 * The interpreter ({@link executeResponsibility}) supplies everything else: the perception
 * stream, reaction latency, the movement integrator, and the decision bookkeeping.
 *
 * Three properties this preserves, deliberately:
 *
 * - **Perception-based.** Predicates receive observables, never a route id. There is no way to
 *   branch on the offense's identity from inside a spec.
 * - **Derived reads, not shared state.** A spec declares which partner feeds it needs and gets
 *   their observables; it never sees another defender's resolved decision. This is the model
 *   `.kiro/steering/reaction-engine.md` mandates for inter-defender calls.
 * - **Params are still the tuning seam.** A spec is generic over its params type, so the same
 *   spec serves CB1 and CB2 with mirrored landmarks — exactly as the hand-written policies did.
 *
 * Pure module: no React, no side effects, no randomness.
 */

import { Position, Observation, Technique, KinematicsParams, DefenderMotionState } from '../types';
import { KeyObservables, buildObservableStream, perceive } from './observables';
import { createMotionState, inferTechnique, stepDefender } from './kinematics';

/** Defender read state. Unchanged from the hand-written policies so results stay compatible. */
export type ReadState = 'READ' | 'MAN' | 'ZONE';

/**
 * Everything a predicate or behavior can see at one tick.
 *
 * Note `key` and `partners` are already latency-delayed by the interpreter — a spec physically
 * cannot read the live frame, so no policy can accidentally become clairvoyant.
 */
export interface ReadContext<P> {
  /** The primary key, as this defender perceives him (delayed by his reaction latency). */
  key: KeyObservables;
  /**
   * Additional receivers this spec declared it needs, in the order declared, each delayed the
   * same way. This is how a call like Palms "Out" is derived — the corner and the safety both
   * observe #2 and each reach their own conclusion, rather than one telling the other.
   */
  partners: KeyObservables[];
  /** This defender's tuning. */
  params: P;
  /** Where the defender is right now. */
  defender: Position;
  /** His current physical state (velocity, technique) — useful for hysteresis. */
  motion: DefenderMotionState;
  /** The state he is in as this tick is evaluated. */
  state: ReadState;
  /** Latch values as of this tick (see {@link ResponsibilitySpec.latches}). */
  latches: Readonly<Record<string, boolean>>;
  /**
   * Binding values as of this tick — which partner index this defender has picked up, per
   * declared binding (see {@link ResponsibilitySpec.bindings}). `null` means nothing bound yet.
   */
  bindings: Readonly<Record<string, number | null>>;
  /**
   * The observables of a bound partner, or null if that binding is unset. The convenience form
   * of `partners[bindings[name]]` — this is how a behavior trails "the crosser he matched"
   * without the interpreter knowing what a crosser is.
   */
  bound: (name: string) => KeyObservables | null;
  /** Index into the feed. */
  tick: number;
  /** Milliseconds since the snap for this tick (real time, not perceived time). */
  t: number;
}

export type ReadPredicate<P> = (ctx: ReadContext<P>) => boolean;

/**
 * Picks WHICH partner to bind to, by index into `ctx.partners`, or null for "none yet".
 *
 * Latches answer "did something happen"; selectors answer "to whom". A defender who matches the
 * first crosser into his hook needs the second question, and a boolean cannot express it.
 */
export type ReadSelector<P> = (ctx: ReadContext<P>) => number | null;

/** What a defender does this tick: where he's going, how fast, and in what technique. */
export interface ReadAction {
  target: Position;
  speed: number;
  /** Omit to let the interpreter infer it from direction and pace. */
  technique?: Technique;
}

export type ReadBehavior<P> = (ctx: ReadContext<P>) => ReadAction;

/** One rule for changing state. Evaluated in declaration order; the first match wins. */
export interface ReadTransition<P> {
  /** States this rule can fire from. */
  from: ReadState[];
  /** State to move to. */
  to: ReadState;
  /** The observable condition. */
  when: ReadPredicate<P>;
  /**
   * Latches to clear when this fires. Used for re-entry: a corner re-attaching to a double move
   * clears the "he broke off short" memory that sent him to ZONE, so a later shallow break can
   * fool him again — which is correct, a receiver can double-move twice.
   */
  clearLatches?: string[];
  /** Bindings to release when this fires — e.g. passing a matched crosser back off. */
  clearBindings?: string[];
  /** Optional label for debugging / the future play-art overlay. */
  note?: string;
}

/**
 * A defender's responsibility, as data.
 *
 * @typeParam P the params type this responsibility is tuned by.
 */
export interface ResponsibilitySpec<P> {
  /** Stable id, e.g. 'mod' (man-only-if-deep), 'wall-read', 'hook'. */
  id: string;
  /** Technique he starts the play in. */
  initialTechnique: Technique;
  /** State he starts the play in. Defaults to 'READ'. */
  initialState?: ReadState;
  /**
   * Sticky observations. Each is evaluated every tick; once true it STAYS true until a
   * transition clears it. This is how "he broke off short" is expressed without the
   * interpreter needing to know what any particular policy remembers.
   */
  latches?: Record<string, ReadPredicate<P>>;
  /**
   * Sticky SELECTIONS. Each is evaluated every tick while unset; the first non-null result is
   * remembered until a transition clears it. This is what lets a spec say "man the first crosser
   * who enters my zone" and then keep trailing that specific receiver — the piece a boolean
   * latch cannot express.
   *
   * Evaluated after latches and before transitions, so a transition can fire on a binding made
   * this same tick.
   */
  bindings?: Record<string, ReadSelector<P>>;
  /** State changes, in priority order. */
  transitions: ReadTransition<P>[];
  /** How he moves in each state. */
  behavior: Record<ReadState, ReadBehavior<P>>;
  /**
   * Optional final adjustment — e.g. the hook read treating "never committed" as ZONE, since a
   * linebacker who was never given a reason to leave simply held his zone.
   */
  finalize?: (state: ReadState) => ReadState;
}

/**
 * One tick of a defender's reasoning, recorded as it happens.
 *
 * The trajectory says WHERE he went; this says WHY. Every field is something a spec already
 * computes, so recording costs nothing beyond the array — and it turns "the safety looks wrong"
 * into "he re-attached at 1.4s because #2 mounted a second vertical push", which is the
 * difference between guessing at a tuning value and knowing which rule fired.
 */
export interface ReadTraceEntry {
  /** Milliseconds since the snap. */
  t: number;
  /** The state he was in for this tick's movement. */
  state: ReadState;
  /** The technique he moved in. */
  technique: Technique;
  /** The `note` of the transition that fired this tick, if one did. */
  transition?: string;
  /** Which latches were set as of this tick. */
  latches: Record<string, boolean>;
  /** Which partners he had bound, by name. */
  bindings: Record<string, number | null>;
  /** Where he ended the tick. */
  position: Position;
}

/**
 * The result of running a responsibility — identical in shape to what the hand-written policies
 * returned, so specs are a drop-in replacement for their callers and their tests.
 */
export interface ResponsibilityResult {
  /** One defender Position per feed sample, starting at his alignment. */
  trajectory: Position[];
  /** The state he ended in. */
  finalState: ReadState;
  /** Milliseconds after the snap when he last changed his mind, or null if he never did. */
  decisionTimeMs: number | null;
  /** The key's depth as he PERCEIVED it at that moment (his latency separates the two). */
  decisionDepthY: number | null;
  /** Tick-by-tick record of his reasoning. Same length as `trajectory` minus the snap frame. */
  trace: ReadTraceEntry[];
}

/**
 * A one-line, human-readable account of what a defender did and why — the text a debug overlay
 * or a failing test should show instead of a bare state letter.
 */
export function explainRead(result: ResponsibilityResult): string {
  const decisive = [...result.trace].reverse().find((e) => e.transition);
  if (!decisive) return `${result.finalState} (never changed his mind)`;
  return `${result.finalState} at ${decisive.t}ms — ${decisive.transition}`;
}

/**
 * Run a responsibility spec against a key's feed and produce the defender's trajectory.
 *
 * This is the skeleton every hand-written policy used to repeat, now written once:
 * build the perception stream, delay it by this defender's latency, evaluate latches, take the
 * first matching transition, ask the behavior where to go, and integrate the movement.
 *
 * @param keyFeed      the primary key's observation feed, in REAL field coordinates.
 * @param partnerFeeds additional feeds the spec declared, in the same order.
 */
export function executeResponsibility<P>(
  spec: ResponsibilitySpec<P>,
  defenderStart: Position,
  keyFeed: Observation[],
  params: P,
  kin: KinematicsParams,
  partnerFeeds: Observation[][] = []
): ResponsibilityResult {
  const trajectory: Position[] = [{ ...defenderStart }];
  let state: ReadState = spec.initialState ?? 'READ';

  const trace: ReadTraceEntry[] = [];

  if (keyFeed.length < 2) {
    return {
      trajectory,
      finalState: spec.finalize ? spec.finalize(state) : state,
      decisionTimeMs: null,
      decisionDepthY: null,
      trace,
    };
  }

  const stream = buildObservableStream(keyFeed);
  const partnerStreams = partnerFeeds.map(buildObservableStream);
  const dtMs = keyFeed[1].t - keyFeed[0].t;

  const latchNames = Object.keys(spec.latches ?? {});
  const latches: Record<string, boolean> = {};
  for (const name of latchNames) latches[name] = false;

  const bindingNames = Object.keys(spec.bindings ?? {});
  const bindings: Record<string, number | null> = {};
  for (const name of bindingNames) bindings[name] = null;

  let motion = createMotionState(defenderStart, spec.initialTechnique);
  let decisionTimeMs: number | null = null;
  let decisionDepthY: number | null = null;

  for (let i = 1; i < keyFeed.length; i++) {
    const dtSec = Math.max((keyFeed[i].t - keyFeed[i - 1].t) / 1000, 1e-6);

    // Perception is delayed here, once, for every spec. A predicate cannot see the live frame.
    const key = perceive(stream, i, dtMs, kin);
    const partners = partnerStreams.map((s) => perceive(s, i, dtMs, kin));

    const boundOf = (name: string): KeyObservables | null => {
      const idx = bindings[name];
      return idx === null || idx === undefined ? null : partners[idx] ?? null;
    };

    const ctx: ReadContext<P> = {
      key,
      partners,
      params,
      defender: motion.position,
      motion,
      state,
      latches,
      bindings,
      bound: boundOf,
      tick: i,
      t: keyFeed[i].t,
    };

    // Latches update BEFORE transitions, so a transition can fire on a break seen this tick.
    for (const name of latchNames) {
      if (!latches[name] && spec.latches![name](ctx)) latches[name] = true;
    }

    // Bindings resolve next — still before transitions, so a rule can fire on a partner picked
    // up this same tick. Once set, a binding sticks until a transition clears it.
    for (const name of bindingNames) {
      if (bindings[name] === null) {
        const picked = spec.bindings![name](ctx);
        if (picked !== null && picked >= 0 && picked < partners.length) bindings[name] = picked;
      }
    }

    // First matching transition wins — declaration order is priority order.
    let firedNote: string | undefined;
    for (const transition of spec.transitions) {
      if (!transition.from.includes(state)) continue;
      if (!transition.when(ctx)) continue;
      state = transition.to;
      for (const name of transition.clearLatches ?? []) latches[name] = false;
      for (const name of transition.clearBindings ?? []) bindings[name] = null;
      // decisionTimeMs is when he ACTED; decisionDepthY is the depth he SAW when he did.
      decisionTimeMs = keyFeed[i].t;
      decisionDepthY = key.position.y;
      firedNote = transition.note;
      break;
    }

    const action = spec.behavior[state]({ ...ctx, state });
    const technique =
      action.technique ?? inferTechnique(motion.position, action.target, action.speed, kin);
    motion = stepDefender(motion, action.target, action.speed, technique, dtSec, kin);
    trajectory.push({ ...motion.position });

    trace.push({
      t: keyFeed[i].t,
      state,
      technique: motion.technique,
      transition: firedNote,
      latches: { ...latches },
      bindings: { ...bindings },
      position: { ...motion.position },
    });
  }

  return {
    trajectory,
    finalState: spec.finalize ? spec.finalize(state) : state,
    decisionTimeMs,
    decisionDepthY,
    trace,
  };
}
